export const ThirdLoginType = {
  Google: 10, // Google账号登录
  Facebook: 11, // Facebook账号登录
  Telegram: 12, // Telegram登录
} as const;

export const Web3LoginType = {
  Metamask: 20, // Metamask钱包登录
  OkxWallet: 21, // OKX Wallet登录
  CoinbaseWallet: 22, // Coinbase Wallet登录
  RabbyWallet: 23, // Rabby Wallet登录
  TrustWallet: 24, // Trust Wallet登录
  Phantom: 25, // Phantom钱包登录
} as const;

export const LoginDeviceType = {
  web: '1',
  android: '2',
  ios: '3',
} as const;

export const web3LoginMap = new Map<string, (typeof Web3LoginType)[keyof typeof Web3LoginType]>([
  ['MetaMask', Web3LoginType.Metamask],
  ['OKX Wallet', Web3LoginType.OkxWallet],
  ['Coinbase Wallet', Web3LoginType.CoinbaseWallet],
  ['Rabby Wallet', Web3LoginType.RabbyWallet],
  ['Trust Wallet', Web3LoginType.TrustWallet],
  ['Phantom', Web3LoginType.Phantom],
]);

export const getWeb3LoginType = (connectorName?: string) => {
  if (!connectorName) return null;
  return web3LoginMap.get(connectorName) || null;
};

export const thirdLoginMap = new Map<string, (typeof ThirdLoginType)[keyof typeof ThirdLoginType]>([
  ['google', ThirdLoginType.Google],
  ['telegram-login', ThirdLoginType.Telegram],
  ['facebook', ThirdLoginType.Facebook],
]);

export const getLoginTypeFromProvider = (provider?: string) => {
  if (!provider) return null;
  return thirdLoginMap.get(provider) || null;
};

/**
 * 登录状态枚举 - 语义化版本
 * 用于替代原来的数字字符串状态码
 */
export enum LoginStatus {
  /** 登录成功 */
  SUCCESS = '1',
  /** 黑名单用户 */
  BLACKLISTED = '2',
  /** 新账号需要选择居住地 */
  NEED_RESIDENCE_SELECTION = '3',
  /** 需要二次认证 */
  NEED_SECOND_AUTH = '4',
  /** 下一次发送需要人机验证 */
  NEED_CAPTCHA_NEXT_TIME = '5',
  /** 下一次发送被冻结一段时间 */
  SEND_FROZEN_NEXT_TIME = '6',
  /** 禁止密码登录,必须使用验证码登录 */
  PASSWORD_FORBIDDEN = '7',
}

/**
 * 验证码状态枚举 - 语义化版本
 */
export enum CodeStatus {
  /** 验证码发送成功 */
  CODE_SUCCESS = 1,
  /** 验证码发送失败，黑名单用户 */
  CODE_FAILED_BLACKLISTED = 2,
  /** 验证码发送失败，格式错误 */
  CODE_FAILED_FORMAT_ERROR = 3,
  /** 验证码发送失败，需要人机验证 */
  CODE_NEED_CAPTCHA = 5,
  /** 验证码发送失败，被冻结一段时间 */
  CODE_SEND_FROZEN = 6,
}

/**
 * 风控状态 blackCheck
 * 1 正常 2 下一次发送需要人机验证  3冻结
 */
export enum BlackCheckStatus {
  /** 风控状态正常 */
  BLACK_STATUS_NO = 1,
  /** 风控下一次发送需要人机验证 */
  BLACK_STATUS_NEED_CAPTCHA = 2,
  /** 风控被冻结 */
  BLACK_STATUS_FROZEN = 3,
  /** 禁止密码登录,必须使用验证码登录 */
  BLACK_STATUS_PASSWORD_FORBIDDEN = 4,
}

/**
 * 登录状态类型 - 保持向后兼容
 */
export type LoginStatusType = `${LoginStatus}`;

/**
 * 用户归因活动来源枚举
 */
export enum AttributeActEnum {
  // 1-rummy、2-裂变转盘、3-代理裂变、4-casino、5-fantasy、99-大厅、101-109为rummy占用
  /** rummy */
  ATTRIBUTE_ACT_RUMMY = '1',
  /** 裂变转盘 */
  ATTRIBUTE_ACT_FISSION = '2',
  /** 代理裂变 */
  ATTRIBUTE_ACT_AGENT = '3',
  /** casino */
  ATTRIBUTE_ACT_CASINO = '4',
  /** fantasy */
  ATTRIBUTE_ACT_FANTASY = '5',
  /** GSL短视频平台 */
  ATTRIBUTE_ACT_GSL = '6',
  /** 休闲游戏-切水果 */
  ATTRIBUTE_ACT_FRUIT_DART = '7',
  /** 大厅 */
  ATTRIBUTE_ACT_HALL = '99',
}

/**
 * 用户归因活动来源
 */
export type AttributeActType = `${AttributeActEnum}`;


/**
 * 登录状态工具函数
 */
export const LoginStatusUtils = {
  isSuccess: (status?: LoginStatusType): boolean => status === LoginStatus.SUCCESS,
  isBlacklisted: (status?: LoginStatusType): boolean => status === LoginStatus.BLACKLISTED,
  needResidenceSelection: (status?: LoginStatusType): boolean => status === LoginStatus.NEED_RESIDENCE_SELECTION,
  needSecondAuth: (status?: LoginStatusType): boolean => status === LoginStatus.NEED_SECOND_AUTH,
  needCaptcha: (status?: LoginStatusType): boolean => status === LoginStatus.NEED_CAPTCHA_NEXT_TIME,
  isFrozen: (status?: LoginStatusType): boolean => status === LoginStatus.SEND_FROZEN_NEXT_TIME,
  isPasswordForbidden: (status?: LoginStatusType): boolean => status === LoginStatus.PASSWORD_FORBIDDEN,
} as const;

/**
 * 验证码状态工具函数
 */
export const CodeStatusUtils = {
  isSuccess: (status?: CodeStatus): boolean => status === CodeStatus.CODE_SUCCESS,
  isFailedBlacklisted: (status?: CodeStatus): boolean => status === CodeStatus.CODE_FAILED_BLACKLISTED,
  isFailedFormatError: (status?: CodeStatus): boolean => status === CodeStatus.CODE_FAILED_FORMAT_ERROR,
  isNeedCaptcha: (status?: CodeStatus): boolean => status === CodeStatus.CODE_NEED_CAPTCHA,
  isSendFrozen: (status?: CodeStatus): boolean => status === CodeStatus.CODE_SEND_FROZEN,
} as const;

export const RSA_PUBLIC_KEY = "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA83CZ8vcM/HWW4pol8m09\nYAs5VnJFa3WdVGNP/cwUADt9OpQAGMB+3Vrs3rvT/Y9sYlZh3zM3SqBhm9n9NprU\n5co/QUR0GKLfu6zUoOmszgeERj4LkIfuPtcmLvXs7s9Z+/imkBut7h0Rs5m2G6Hp\nJG9Sb8jXuGfLLYTEm6/LLdhdZr0D8loUQXJgnA3XcNOz8I5d2hiJC3w+EgPVIv28\neJpPtmisguNq4N91aaUxcdIShiuaNq4TtuTdfrEfZ2Umh6dTCT8BKMt+ta579/oQ\n12jfeJrEWwWyEFZWGfcsVUbFW41CcP+/DCEG+dcMbBrfeo3J7jJYqTalOV+/naES\nRQIDAQAB";
